/** @format */

import Sortable from 'sortablejs'

const sortableRegistry = new WeakMap()
let inFlightItem

function chainHandler(builtinHandler, userHandler) {
	return function (evt) {
		builtinHandler(evt)
		if (typeof userHandler === 'function') userHandler(evt)
	}
}

export function createSortableList(element, opts) {
	if (!element) {
		throw new Error('createSortableList: element is required')
	}
	if (!opts || typeof opts.getItems !== 'function' || typeof opts.setItems !== 'function') {
		throw new Error('createSortableList: getItems and setItems are required functions')
	}

	const { getItems, setItems, group, ...sortableOptions } = opts

	const userOnStart = sortableOptions.onStart
	const userOnAdd = sortableOptions.onAdd
	const userOnRemove = sortableOptions.onRemove
	const userOnUpdate = sortableOptions.onUpdate
	const userOnEnd = sortableOptions.onEnd

	const builtinOnStart = (evt) => {
		const sourceEntry = sortableRegistry.get(evt.from)
		if (sourceEntry && typeof sourceEntry.getItems === 'function') {
			const sourceItems = sourceEntry.getItems()
			inFlightItem = sourceItems ? sourceItems[evt.oldIndex] : undefined
		} else {
			inFlightItem = undefined
		}
	}

	const builtinOnAdd = (evt) => {
		if (inFlightItem !== undefined) {
			const items = [...getItems()]
			items.splice(evt.newIndex, 0, inFlightItem)
			setItems(items)
		}
		if (evt.item && evt.item.parentNode) {
			evt.item.parentNode.removeChild(evt.item)
		}
	}

	const builtinOnRemove = (evt) => {
		// Sortable fires onRemove with pullMode='clone' even when the source keeps its item.
		// Splicing the source array here would desync model (N-1) from DOM (N).
		// Note for future object-array consumers: in clone mode the in-flight value
		// spliced into the target shares the same JS reference the source still holds —
		// callers using object items must deep-clone themselves if independent identity matters.
		if (evt.pullMode === 'clone') return
		const items = [...getItems()]
		items.splice(evt.oldIndex, 1)
		setItems(items)
	}

	const builtinOnUpdate = (evt) => {
		const parent = evt.item && evt.item.parentNode
		if (parent) {
			parent.removeChild(evt.item)
			const refChild = parent.children[evt.oldIndex]
			if (refChild) parent.insertBefore(evt.item, refChild)
			else parent.appendChild(evt.item)
		}
		const items = [...getItems()]
		const [moved] = items.splice(evt.oldIndex, 1)
		items.splice(evt.newIndex, 0, moved)
		setItems(items)
	}

	const builtinOnEnd = () => {
		inFlightItem = undefined
	}

	const finalOptions = {
		animation: 150,
		...sortableOptions,
		group,
		onStart: chainHandler(builtinOnStart, userOnStart),
		onAdd: chainHandler(builtinOnAdd, userOnAdd),
		onRemove: chainHandler(builtinOnRemove, userOnRemove),
		onUpdate: chainHandler(builtinOnUpdate, userOnUpdate),
		onEnd: chainHandler(builtinOnEnd, userOnEnd),
	}

	const instance = new Sortable(element, finalOptions)
	sortableRegistry.set(element, { getItems, setItems, sortable: instance })

	return {
		sortable: instance,
		destroy() {
			sortableRegistry.delete(element)
			instance.destroy()
		},
	}
}

export function registerSortablePlugin(plugin) {
	Sortable.mount(plugin)
}
